import {
  Button,
  ButtonGroup,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Tooltip,
} from '@heroui/react';
import {
  ArrowLeft,
  ChevronDown,
  Download,
  Maximize,
  Minimize,
  Redo2,
  Save,
  Settings,
  Undo2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { findFormat, findPlatform } from '../../config/platforms';
import { SavedKind, useAdsGeneratorStore } from '../../model/store';
import { ExportFormat } from '../../utils/export';
import { GENERATOR_ROUTES } from '../PlatformStep/GeneratorTabs';
import { FormatBadge, TopBarGroup, TopBarWrapper } from './Editor.styled';
import { SceneWarnings } from './SceneWarnings';

interface EditorTopBarProps {
  onBack: () => void;
  /** `asNew` wymusza nowy wpis zamiast nadpisania otwartej kreacji. */
  onSave: (kind: SavedKind, asNew?: boolean) => void;
  isSaving: boolean;
  onDownload: (format: ExportFormat, scale: number) => void;
  isExporting: boolean;
  onFit: () => void;
  onZoomStep: (factor: number) => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
}

const EXPORT_OPTIONS: { key: string; label: string; format: ExportFormat; scale: number }[] = [
  { key: 'png-1', label: 'PNG w rozmiarze formatu', format: 'png', scale: 1 },
  { key: 'png-2', label: 'PNG dwa razy większy', format: 'png', scale: 2 },
  { key: 'jpg-1', label: 'JPG w rozmiarze formatu', format: 'jpeg', scale: 1 },
];

const SAVE_OPTIONS = [
  { key: 'creative-new', label: 'Zapisz jako nową kreację', kind: 'creative' as SavedKind, asNew: true },
  { key: 'template', label: 'Zapisz jako szablon', kind: 'template' as SavedKind, asNew: true },
];

export const EditorTopBar: FC<EditorTopBarProps> = ({
  onBack,
  onSave,
  isSaving,
  onDownload,
  isExporting,
  onFit,
  onZoomStep,
  isFullscreen,
  onToggleFullscreen,
}) => {
  const scene = useAdsGeneratorStore((state) => state.scene);
  const zoom = useAdsGeneratorStore((state) => state.zoom);
  const undo = useAdsGeneratorStore((state) => state.undo);
  const redo = useAdsGeneratorStore((state) => state.redo);
  const canUndo = useAdsGeneratorStore((state) => state.past.length > 0);
  const canRedo = useAdsGeneratorStore((state) => state.future.length > 0);
  const saved = useAdsGeneratorStore((state) => state.saved);
  const navigate = useNavigate();

  if (!scene) return null;

  const platform = findPlatform(scene.platformId);
  const format = findFormat(scene.platformId, scene.formatId);

  return (
    <TopBarWrapper>
      <TopBarGroup>
        <Button size="sm" radius="sm" variant="light" startContent={<ArrowLeft size={16} />} onPress={onBack}>
          Zmień format
        </Button>
        <FormatBadge>
          <strong>{`${platform?.name ?? ''}, ${format?.label ?? ''}`}</strong>
          <span>{`${scene.width} x ${scene.height} px`}</span>
        </FormatBadge>
      </TopBarGroup>

      <TopBarGroup>
        <Tooltip content="Cofnij (Ctrl+Z)" size="sm">
          <Button size="sm" radius="sm" isIconOnly variant="flat" aria-label="Cofnij" isDisabled={!canUndo} onPress={undo}>
            <Undo2 size={16} />
          </Button>
        </Tooltip>
        <Tooltip content="Ponów (Ctrl+Shift+Z)" size="sm">
          <Button size="sm" radius="sm" isIconOnly variant="flat" aria-label="Ponów" isDisabled={!canRedo} onPress={redo}>
            <Redo2 size={16} />
          </Button>
        </Tooltip>

        <ButtonGroup size="sm" radius="sm" variant="flat">
          <Button isIconOnly aria-label="Pomniejsz" onPress={() => onZoomStep(1 / 1.2)}>
            <ZoomOut size={16} />
          </Button>
          {/* Kliknięcie w procenty wraca do dopasowania planszy do okna. */}
          <Button className="min-w-16" title="Dopasuj do okna" onPress={onFit}>
            {`${Math.round(zoom * 100)}%`}
          </Button>
          <Button isIconOnly aria-label="Powiększ" onPress={() => onZoomStep(1.2)}>
            <ZoomIn size={16} />
          </Button>
        </ButtonGroup>

        <Tooltip content="Ustawienia generatora" size="sm">
          <Button
            size="sm"
            radius="sm"
            isIconOnly
            variant="flat"
            aria-label="Ustawienia generatora"
            onPress={() => navigate(GENERATOR_ROUTES.settings)}
          >
            <Settings size={16} />
          </Button>
        </Tooltip>

        <SceneWarnings />

        <Tooltip content={isFullscreen ? 'Wyjdź z pełnego ekranu' : 'Pełny ekran'} size="sm">
          <Button
            size="sm"
            radius="sm"
            isIconOnly
            aria-label="Pełny ekran"
            variant={isFullscreen ? 'solid' : 'flat'}
            color={isFullscreen ? 'primary' : 'default'}
            onPress={onToggleFullscreen}
          >
            {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
          </Button>
        </Tooltip>

        <ButtonGroup size="sm" radius="sm">
          <Button
            color="success"
            className="text-white"
            startContent={<Save size={16} />}
            isLoading={isSaving}
            onPress={() => onSave(saved?.kind ?? 'creative')}
          >
            {saved ? 'Zapisz zmiany' : 'Zapisz'}
          </Button>
          <Dropdown placement="bottom-end">
            <DropdownTrigger>
              <Button isIconOnly color="success" className="text-white" aria-label="Inne opcje zapisu">
                <ChevronDown size={16} />
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Opcje zapisu"
              onAction={(key) => {
                const option = SAVE_OPTIONS.find((item) => item.key === key);
                if (option) onSave(option.kind, option.asNew);
              }}
            >
              {SAVE_OPTIONS.map((option) => (
                <DropdownItem key={option.key}>{option.label}</DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </ButtonGroup>

        <ButtonGroup size="sm" radius="sm">
          <Button
            className="bg-black text-white"
            startContent={<Download size={16} />}
            isLoading={isExporting}
            onPress={() => onDownload('png', 1)}
          >
            Pobierz PNG
          </Button>
          <Dropdown placement="bottom-end">
            <DropdownTrigger>
              <Button isIconOnly className="bg-black text-white" aria-label="Inne formaty pliku">
                <ChevronDown size={16} />
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Format pliku"
              onAction={(key) => {
                const option = EXPORT_OPTIONS.find((item) => item.key === key);
                if (option) onDownload(option.format, option.scale);
              }}
            >
              {EXPORT_OPTIONS.map((option) => (
                <DropdownItem key={option.key}>{option.label}</DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </ButtonGroup>
      </TopBarGroup>
    </TopBarWrapper>
  );
};
